import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiCallsHelper } from 'app/modules/common/helpers/ApiCallsHelper';
import { PageLoaderService } from 'app/modules/common/services/page-loader.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { Baja } from 'app/modules/common/types/baja.type';
import { TipoBaja } from 'app/modules/common/types/tipo-baja.type';
import { Trabajador } from 'app/modules/common/types/employee/trabajador.type';
import { EmployeeService } from 'app/modules/employee/services/employee.service';
import {
    ChangeLeaveTypeModel,
    LeaveService,
} from 'app/modules/leave/services/leave.service';
import { TaskManagementService } from 'app/modules/task/services/task-management.service';
import { PendingTask } from '../../actuaciones/create-actuationes/pending-task/pending-task.component';

export type ChangeLeaveTypeDialogData = {
    leaveId: number;
    employeeId: number;
};

@Component({
    selector: 'app-change-leave-type-dialog',
    templateUrl: './change-leave-type-dialog.component.html',
    styleUrls: ['./change-leave-type-dialog.component.scss'],
    standalone: false
})
export class ChangeLeaveTypeDialogComponent implements OnInit {
    leaveInfo: Baja;
    pendingTasks: PendingTask[] = [];
    leaveTypes: TipoBaja[] = [];
    selectedLeaveType: TipoBaja;
    form: UntypedFormGroup;

    constructor(
        private leaveService: LeaveService,
        private employeeService: EmployeeService,
        private taskManagementService: TaskManagementService,
        private pageLoader: PageLoaderService,
        private sharedService: SharedService,
        private dialogRef: MatDialogRef<ChangeLeaveTypeDialogComponent>,
        private fb: UntypedFormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: ChangeLeaveTypeDialogData
    ) {}

    ngOnInit(): void {
        setTimeout(() => this.pageLoader.ShowPageLoader());
        this.initData(this.data.leaveId)
            .catch((err) => {
                console.error(err);
                this.sharedService.ShowSnackBar(
                    'Lo siento, la carga de datos ha fallado'
                );
                this.close();
            })
            .finally(() => setTimeout(() => this.pageLoader.HidePageLoader()));

        this.initForm();
    }

    private async initData(leaveId: number): Promise<void> {
        this.leaveInfo = await ApiCallsHelper.toPromise(
            this.leaveService.GetLeaveById(leaveId.toString())
        );

        const employee: Trabajador = await ApiCallsHelper.toPromise(
            this.employeeService.GetTrabajador(this.data.employeeId)
        );

        this.leaveTypes = await ApiCallsHelper.toPromise(
            this.leaveService.GetManagedUnmanagedLeaveTypes(employee.empresaId)
        );
    }

    private initForm(): void {
        this.form = this.fb.group({
            leaveType: [null, Validators.required],
        });

        this.form
            .get('leaveType')
            .valueChanges.subscribe((value) => this.onLeaveTypeChange(value));
    }

    public close() {
        this.dialogRef.close(false);
    }

    private onLeaveTypeChange(leaveType: string): void {
        this.selectedLeaveType = this.leaveTypes.find(
            (t) => t.value === leaveType
        );
        this.managePendingTasks(!this.selectedLeaveType.managed);
    }

    private managePendingTasks(check: boolean) {
        this.pendingTasks.forEach((task) => (task.checked = check));
    }

    public async onSubmit() {
        if (!this.areFormsvalid()) return;

        const model = this.buildModel();

        this.pageLoader.ShowPageLoader();
        try {
            await this.changeLeaveType(this.data.leaveId, model);
            this.pageLoader.HidePageLoader();
            this.sharedService.ShowSnackBar(
                'El tipo de permiso se ha cambiado con éxito'
            );
            this.dialogRef.close(true);
        } catch (error) {
            console.error(error);
            this.sharedService.ShowSnackBar(
                'Lo siento, el cambio de tipo de licencia falló'
            );
            this.pageLoader.HidePageLoader();
        }
    }

    private async changeLeaveType(
        leaveId: number,
        changeLeaveTypeModel: ChangeLeaveTypeModel
    ) {
        await ApiCallsHelper.toPromise(
            this.leaveService.changeLeaveType(leaveId, changeLeaveTypeModel)
        );
    }

    /**
     * There is main form and next task form. Both are validated here according to selected leave type.
     */
    private areFormsvalid(): boolean {
        this.form.markAllAsTouched();

        if (!this.selectedLeaveType) return false;

        if (!this.leaveInfo.falta && this.selectedLeaveType.managed)
            this.taskManagementService.TaskManagementForm.markAllAsTouched();

        return (
            this.form.valid &&
            (this.leaveInfo.falta != null ||
                !this.selectedLeaveType.managed ||
                this.taskManagementService.TaskManagementForm.valid)
        );
    }

    /**
     * Build change leave type model
     */
    private buildModel(): ChangeLeaveTypeModel {
        const nextTask =
            !this.leaveInfo.falta && this.selectedLeaveType.managed
                ? this.taskManagementService.getTaskSaveModel()
                : null;

        const pendingTasks = !this.leaveInfo.falta
            ? this.pendingTasks.filter((t) => t.checked)
            : null;

        const model: ChangeLeaveTypeModel = {
            leaveType: this.selectedLeaveType.value,
            nextTask,
            pendingTasks,
        };

        return model;
    }
}
